import { IntegrationData } from "../types"

export const cventData: IntegrationData = {
  platform: {
    slug: "cvent",
    name: "Cvent",
    description:
      "Turn every attendee into an event promoter with Premagic's native Cvent integration",
    shortDescription: "Native integration for event management",
    logoUrl: "https://companieslogo.com/img/orig/CVT-1a4927ff.svg?t=1720244491",
    websiteUrl: "https://www.cvent.com/",
    category: "event-management",
    tags: ["Event Management", "Native Integration", "Bi-Directional Sync"],
    isComingSoon: false,
  },
  heroTitle: "Turn Every Attendee Into an Event Promoter",
  heroDescription:
    "We're excited to announce our native integration with Cvent, the world's leading event management platform. This integration transforms your registered attendees into active event promoters – driving referrals and boosting registrations through personalized, shareable content.",
  challengeTitle: "The Challenge",
  challengeDescription:
    "You've set up your event on Cvent. Registrations are trickling in. But how do you turn that trickle into a flood? Traditional marketing can only take you so far. The most powerful driver of event registrations has always been word-of-mouth – attendees sharing with their networks.",
  challengePoints: [
    {
      title: "Limited Organic Reach",
      description:
        "Most attendees don't actively promote your event, even if they're excited about it. They need a nudge. They need something shareable. They need it to be effortless.",
    },
    {
      title: "Manual Poster Creation",
      description:
        "Creating personalized marketing materials for each attendee is time-consuming and requires design skills. Without automation, you can't scale poster creation to your entire attendee base.",
    },
    {
      title: "Missed Referral Opportunities",
      description:
        "The moment after registration is when attendees are most excited and most likely to share. Without a tool embedded in your registration flow, you miss this critical window to capture referrals.",
    },
  ],
  capabilities: [
    {
      title: "Embed the Poster Widget on Your Cvent Registration Page",
      description:
        "Add Premagic's poster creation widget directly to your Cvent registration flow. The moment someone registers, they can create and share a personalized event poster – while their excitement is at its peak.",
      features: [
        "Seamless embedding with no coding required",
        "Matches your event branding",
        "Works on desktop and mobile",
        "Captures the attendee at the perfect moment to share",
      ],
    },
    {
      title: "Auto-Generate Personalized Posters from Attendee Data",
      description:
        "Because we sync all attendee information from Cvent, we can automatically create pre-filled posters for every registrant. Each poster is personalized with attendee's name, company, job title, and profile photo.",
      features: [
        "Attendee's name and company",
        "Job title and profile photo",
        "Custom messaging and event details",
        "No manual work or design skills needed",
      ],
    },
    {
      title: "Launch Campaigns to Drive Sharing and Referrals",
      description:
        "Don't wait for attendees to share on their own. Proactively send poster campaigns to nudge them into action with email campaigns, reminder sequences, and social sharing CTAs.",
      features: [
        "Email campaigns with personalized posters",
        "Reminder sequences leading up to the event",
        "One-click social sharing CTAs",
        "Track which attendees are driving the most referrals",
      ],
    },
    {
      title: "Complete Attendee Sync from Cvent",
      description:
        "The integration automatically imports your full attendee roster, including names, contact information, company, job title, registration type, profile photos, and social media profiles.",
      features: [
        "Full name and contact information",
        "Company and job title",
        "Registration type and category",
        "Profile photos and social media profiles",
      ],
    },
    {
      title: "Bi-Directional Data Flow",
      description:
        "Premagic syncs valuable data back to your Cvent records, including personal gallery URLs, poster sharing links, and registration IDs for seamless cross-platform tracking.",
      features: [
        "Personal Gallery URLs for post-event photo access",
        "Poster Sharing Links to track engagement",
        "Registration IDs for seamless cross-platform tracking",
      ],
    },
    {
      title: "AI-Powered Photo Distribution",
      description:
        "After the event, the same integration powers personalized photo delivery. Attendee profile photos enable instant facial recognition, and event photos are automatically tagged and distributed.",
      features: [
        "Attendee profile photos enable facial recognition",
        "Event photos automatically tagged and distributed",
        "Every attendee gets their own photo gallery",
        "Drive post-event engagement and social sharing",
      ],
    },
  ],
  flywheelTitle: "The Referral Flywheel",
  flywheelDescription:
    "Here's how it works in practice: Each registration multiplies your reach.",
  flywheelSteps: [
    {
      number: 1,
      title: "Register",
      description: "Attendee signs up on Cvent",
    },
    {
      number: 2,
      title: "Create",
      description:
        "Poster widget appears; attendee creates personalized poster",
    },
    {
      number: 3,
      title: "Share",
      description: "Attendee shares to LinkedIn, Twitter, WhatsApp, email",
    },
    {
      number: 4,
      title: "Discover",
      description: "Their network sees the poster with event details",
    },
    {
      number: 5,
      title: "Register",
      description: "New attendees sign up through shared links",
    },
    {
      number: 6,
      title: "Repeat",
      description: "Every new registrant becomes another promoter",
    },
  ],
  howItWorksSteps: [
    {
      number: 1,
      title: "Connect",
      description: "Link your Cvent account to Premagic",
    },
    {
      number: 2,
      title: "Embed",
      description: "Add the poster widget to your Cvent registration page",
    },
    {
      number: 3,
      title: "Sync",
      description: "Import all attendees automatically",
    },
    {
      number: 4,
      title: "Campaign",
      description: "Send personalized poster campaigns to drive sharing",
    },
    {
      number: 5,
      title: "Track",
      description: "Monitor referrals and registration growth",
    },
    {
      number: 6,
      title: "Deliver",
      description:
        "After the event, distribute photos using the same attendee data",
    },
  ],
  capabilitiesTable: [
    {
      feature: "Poster Widget Embedding",
      description:
        "Add shareable poster creation to your Cvent registration page",
    },
    {
      feature: "Pre-filled Posters",
      description:
        "Auto-generate personalized posters from synced attendee data",
    },
    {
      feature: "Campaign Distribution",
      description: "Send poster campaigns to drive sharing and referrals",
    },
    {
      feature: "Attendee Sync",
      description:
        "Full roster import including names, companies, titles, and profile photos",
    },
    {
      feature: "Profile Photo Import",
      description:
        "Use Cvent profile pictures for poster personalization and facial recognition",
    },
    {
      feature: "Custom Field Mapping",
      description: "Sync event questions and registration data for targeting",
    },
    {
      feature: "Bi-directional Sync",
      description: "Push gallery and poster links back to Cvent records",
    },
    {
      feature: "AI Photo Distribution",
      description: "Post-event photo delivery powered by synced attendee data",
    },
  ],
  targetAudience: [
    {
      title: "Corporate Event Planners",
      description:
        "Who need to hit registration targets for conferences and summits",
    },
    {
      title: "Event Marketing Teams",
      description: "Looking to amplify reach without increasing ad spend",
    },
    {
      title: "Enterprise Organizations",
      description:
        "Running large-scale events on Cvent who want organic growth",
    },
    {
      title: "Event Agencies",
      description:
        "Managing client events and delivering measurable registration results",
    },
  ],
  ctaTitle: "Ready to Turn Registrations Into Referrals?",
  ctaDescription:
    "Your attendees are your best marketers. They just need the right tools. With Premagic's Cvent integration, you can capture the moment, personalize at scale, activate your audience, and drive registrations. Stop relying only on ads. Start leveraging your attendees.",
  seo: {
    title: "Premagic + Cvent Integration | Turn Attendees Into Event Promoters",
    description:
      "Transform your Cvent registrations into referrals with Premagic's native integration. Auto-generate personalized posters, sync attendee data, and drive event registrations through word-of-mouth marketing.",
    keywords: [
      "cvent",
      "event management",
      "attendee advocacy",
      "event marketing",
      "referral marketing",
      "poster creation",
      "event promotion",
      "registration growth",
    ],
  },
}
